import HttpService from "@core/services/http/http.service";
import axios from "axios";
import {
  Book,
  BookDetail,
  BookEbookFile,
  CreateBook,
  GetDetailResponse,
  GetListResponse,
  LabelValue,
  RequestParamsBook,
  ThumbnailByAIPayload,
} from "@app/types";
import { ACCESS_TOKEN_KEY } from "@app/constants";
import {
  RequestContentType,
  RequestParams,
  Signal,
} from "@core/services/http/http.type";
import { Environment } from "@environments/environment";
import StorageService from "@core/services/storage";

class _BookService {
  public getList(
    requestParams?: RequestParamsBook,
    signal?: Signal,
    isShowLoading?: boolean
  ) {
    return HttpService.get<GetListResponse<Book>>("/admin/api/v3/books", {
      queryParams: { ...requestParams },
      signal,
      isShowLoading,
    });
  }

  public get(bookId: number, signal?: Signal, isShowLoading?: boolean) {
    return HttpService.get<GetDetailResponse<BookDetail>>(
      `/admin/api/v3/books/${bookId}`,
      {
        signal,
        isShowLoading,
      }
    );
  }

  public create(createBook: CreateBook) {
    return HttpService.post("/admin/api/v3/books", {
      body: { ...createBook },
    });
  }

  public update(bookId: number, updateBook: CreateBook, isShowLoading?: boolean) {
    return HttpService.patch(`/admin/api/v3/books/${bookId}`, {
      body: { ...updateBook },
      isShowLoading,
    });
  }

  public getEbookFile(bookId: number, isShowLoading?: boolean) {
    return HttpService.get<GetDetailResponse<BookEbookFile>>(
      `/admin/api/v3/books/${bookId}/ebook-file`,
      {
        isShowLoading,
      }
    );
  }

  public uploadEbookFile(bookId: number, file: File, isShowLoading?: boolean) {
    return HttpService.post<GetDetailResponse<BookEbookFile>>(
      `/admin/api/v3/books/${bookId}/ebook-file`,
      {
        body: { file },
        requestContentType: RequestContentType.MULTIPART,
        isShowLoading,
      }
    );
  }

  public deleteEbookFile(bookId: number, isShowLoading?: boolean) {
    return HttpService.delete<GetDetailResponse<BookEbookFile>>(
      `/admin/api/v3/books/${bookId}/ebook-file`,
      {
        isShowLoading,
      }
    );
  }

  public reprocessEbookFile(bookId: number, isShowLoading?: boolean) {
    return HttpService.post<GetDetailResponse<BookEbookFile>>(
      `/admin/api/v3/books/${bookId}/ebook-file/reprocess`,
      {
        isShowLoading,
      }
    );
  }

  public async previewEbookFile(bookId: number) {
    const accessToken = StorageService.get(ACCESS_TOKEN_KEY);
    const response = await axios.get<Blob>(
      `${Environment.BASE_API}/admin/api/v3/books/${bookId}/ebook-file/preview`,
      {
        responseType: "blob",
        headers: accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : undefined,
      }
    );

    return response.data;
  }

  public delete(bookId: number) {
    return HttpService.delete(`/admin/api/v3/books/${bookId}`);
  }

  public deletePermanently(bookId: number) {
    return HttpService.delete(
      `/admin/api/v2/books/delete-permanently/${bookId}`
    );
  }

  public restore(bookId: number) {
    return HttpService.patch(`/admin/api/v2/books/restore/${bookId}`);
  }

  public changeBookStatus(bookId: number) {
    return HttpService.post(`/admin/api/v1/books/toggle-status/${bookId}`);
  }

  public changeBookReleaseType(bookId: number) {
    return HttpService.patch(
      `/admin/api/v2/books/toggle-release-type/${bookId}`
    );
  }

  public getRetailBooks(requestParams?: RequestParams, signal?: Signal) {
    return HttpService.get<GetListResponse<LabelValue>>(
      "/admin/api/v2/books/retail",
      {
        queryParams: { ...requestParams },
        signal,
      }
    );
  }

  public giveBookToUser(bookId: number, userId: number) {
    return HttpService.post(`/admin/api/v2/books/give/${bookId}/${userId}`);
  }

  public getBookDropdown(
    requestParams?: RequestParamsBook,
    signal?: Signal,
    isShowLoading?: boolean
  ) {
    return HttpService.get<GetListResponse<Book>>("/admin/api/v2/books", {
      queryParams: { ...requestParams },
      signal,
      isShowLoading,
    });
  }

  public createTranscription(bookId: number) {
    return HttpService.post(`/admin/api/v2/books/transcribe/${bookId}`);
  }

  public approveContract(bookId: number) {
    return HttpService.post(`/admin/api/v3/books/${bookId}/request-approve-contract`);
  }

  public thumbnailByAI(bookId: number, payload: ThumbnailByAIPayload, isShowLoading?: boolean) {
    return HttpService.post(`/admin/api/v3/books/${bookId}/thumbnail-ai`, {
      body: { ...payload },
      isShowLoading,
    });
  }
}

const BookService = new _BookService();

export default BookService;
